/**
 * Post-processing for APA reference strings shown in the editor and source library.
 */

export function normalizeUrlProtocol(url) {
	const raw = String(url || '').trim();
	if (!raw || raw === 'URL') return raw;
	if (/^https?:\/\//i.test(raw)) return raw;
	if (/^doi:/i.test(raw)) return raw;
	if (/^www\./i.test(raw)) return `https://${raw}`;
	if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(raw)) return `https://${raw}`;
	return raw;
}

function escapeHtmlAttr(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;');
}

function escapeHtmlText(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/** True when URL is (or normalizes to) http(s) only. */
export function isSafeHttpUrl(url) {
	const raw = String(url || '').trim();
	if (!raw || raw === 'URL') return false;
	if (/^(javascript|data|vbscript|file):/i.test(raw)) return false;
	const normalized = normalizeUrlProtocol(raw);
	if (!/^https?:\/\//i.test(normalized)) return false;
	try {
		const parsed = new URL(normalized);
		return parsed.protocol === 'http:' || parsed.protocol === 'https:';
	} catch {
		return false;
	}
}

/**
 * Bare host.tld or host.tld/path — not www./http(s), and not email local-parts.
 * Reuses the same host shape as normalizeUrlProtocol.
 */
const BARE_DOMAIN_URL_RE =
	/(https?:\/\/[^\s<]+|www\.[^\s<]+|(?<![\w@./-])[\w.-]+\.[a-z]{2,}(?:\/[^\s<]*)?)/gi;

/**
 * Wrap bare URLs in anchor tags so TinyMCE reference entries are clickable.
 */
export function linkifyReferenceUrls(html) {
	if (!html || typeof html !== 'string') return html;
	if (html.includes('<a ')) {
		// Still normalize hrefs missing protocol inside existing anchors
		return html.replace(/href="([^"]+)"/gi, (match, href) => {
			const normalized = normalizeUrlProtocol(href);
			return normalized === href ? match : `href="${escapeHtmlAttr(normalized)}"`;
		});
	}

	const parts = html.split(/(<[^>]+>)/g);
	return parts
		.map((part) => {
			if (part.startsWith('<')) return part;
			return part.replace(BARE_DOMAIN_URL_RE, (rawUrl) => {
				if (!isSafeHttpUrl(rawUrl)) return rawUrl;
				const href = normalizeUrlProtocol(rawUrl);
				const display = href;
				return `<a href="${escapeHtmlAttr(href)}" target="_blank" rel="noopener noreferrer">${escapeHtmlText(display)}</a>`;
			});
		})
		.join('');
}

export function finalizeReferenceHtml(html) {
	if (!html || typeof html !== 'string') return html;
	if (/personal communications are cited only in-text/i.test(html)) return html;
	return linkifyReferenceUrls(html);
}

/**
 * Open safe http(s) links on plain click (TinyMCE suppresses default navigation).
 * @returns {boolean} true when the click was handled
 */
export function handleEditorExternalLinkClick(event, openWindow) {
	if (!event || event.defaultPrevented) return false;
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
	if (typeof event.button === 'number' && event.button !== 0) return false;

	let node = event.target;
	let anchor = null;
	while (node) {
		const tag = node.tagName || node.nodeName;
		if (
			tag &&
			String(tag).toUpperCase() === 'A' &&
			typeof node.getAttribute === 'function' &&
			node.getAttribute('href')
		) {
			anchor = node;
			break;
		}
		node = node.parentElement || node.parentNode || null;
	}
	if (!anchor) return false;

	const href = anchor.getAttribute('href');
	if (!isSafeHttpUrl(href)) return false;

	const url = normalizeUrlProtocol(href);
	if (typeof event.preventDefault === 'function') event.preventDefault();
	if (typeof event.stopPropagation === 'function') event.stopPropagation();

	const open =
		typeof openWindow === 'function'
			? openWindow
			: typeof window !== 'undefined' && typeof window.open === 'function'
				? window.open.bind(window)
				: null;
	if (open) open(url, '_blank', 'noopener,noreferrer');
	return true;
}

/**
 * APA reference list sort key: first author surname or first significant group-author word.
 */
export function apaReferenceSortKey(referenceText) {
	const plain = String(referenceText || '')
		.replace(/<[^>]+>/g, '')
		.replace(/\s+/g, ' ')
		.trim();
	if (!plain) return '';

	let authorPart = plain.split('(')[0].replace(/\.\s*$/, '').trim();
	authorPart = authorPart.split('&')[0].trim();

	if (authorPart.includes(',')) {
		return authorPart.split(',')[0].trim().toLowerCase();
	}

	return authorPart.replace(/^(the|a|an)\s+/i, '').trim().toLowerCase();
}

const REFERENCES_HEADING_SELECTOR =
	'h2.wf-references-heading, p.wf-references-heading, [data-wf-references-heading]';

/**
 * Ensure the References back-matter section has a visible centered heading.
 */
export function ensureReferencesSectionHeading(editor, sectionBody, title = 'References') {
	if (!editor || !sectionBody) return;
	if (sectionBody.querySelector(REFERENCES_HEADING_SELECTOR)) return;

	const heading = editor.dom.create(
		'h2',
		{
			class: 'wf-references-heading',
			'data-wf-references-heading': 'true',
			style: 'text-align: center; font-weight: bold; margin-bottom: 1em;'
		},
		title
	);

	const firstRef = sectionBody.querySelector('p.reference-entry');
	if (firstRef) {
		sectionBody.insertBefore(heading, firstRef);
	} else {
		sectionBody.insertBefore(heading, sectionBody.firstChild);
	}
}
